// Appels d'API de l'ÉVOLUTION DE L'ADOPTION et des ALERTES (migration 059).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

export type Frequency = 'hebdomadaire' | 'mensuelle' | 'trimestrielle' | 'aucune';

export interface AlertRules {
  newMeasure: { enabled: boolean };
  scoreDrop: { enabled: boolean; points: number };
  pillarBelow: { enabled: boolean; threshold: number };
  recoPending: { enabled: boolean; days: number };
}

export interface AlertSettings {
  frequency: Frequency;
  rules: AlertRules;
  recipients: string[];
  configured: boolean;
  lastRunAt: string | null;
  nextRunAt: string | null;
}

export interface EvolutionPoint {
  month: string;
  takenAt: string;
  global: number | null;
  confidence: number | null;
  pillars: { pillarId: string; name: string; score: number | null; isCapped: boolean }[];
  recommendations: { a_tester: number; en_test: number; confirmee: number; ecartee: number };
  trigger: string;
}

export interface AlertEvent {
  id: string;
  kind: string;
  message: string;
  severity: 'info' | 'attention' | 'grave';
  at: string;
}

export interface EvolutionResponse {
  series: EvolutionPoint[];
  /** Historique d'exemple (projet de démonstration). */
  sample: boolean;
  events: AlertEvent[];
  settings: AlertSettings;
  canWrite: boolean;
  mail: boolean;
}

const key = (projectId: string) => ['adoption-history', projectId] as const;

export function useAdoptionHistory(projectId: string) {
  return useQuery({
    queryKey: key(projectId),
    queryFn: () => api.get<EvolutionResponse>(`/api/projects/${encodeURIComponent(projectId)}/adoption/history`),
    staleTime: 30 * 1000,
  });
}

export function useMeasureNow(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ alerts: { kind: string; message: string; severity: string }[]; emailed: boolean; global: number | null }>(
      `/api/projects/${encodeURIComponent(projectId)}/adoption/measure`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(projectId) });
      void qc.invalidateQueries({ queryKey: ['adoption', projectId] });
    },
  });
}

export function useAlertSettings(projectId: string) {
  return useQuery({
    queryKey: ['alerts', projectId] as const,
    queryFn: () => api.get<{ settings: AlertSettings; canWrite: boolean; mail: boolean }>(`/api/projects/${encodeURIComponent(projectId)}/alerts`),
    staleTime: 30 * 1000,
  });
}

export function useSaveAlertSettings(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: { frequency: Frequency; rules: AlertRules; recipients: string[] }) =>
      api.put<{ settings: AlertSettings }>(`/api/projects/${encodeURIComponent(projectId)}/alerts`, s),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['alerts', projectId] });
      void qc.invalidateQueries({ queryKey: key(projectId) });
    },
  });
}
